import React from "react";
import { connect } from "react-redux";
import { Input, Select } from "../../Input";
import { Button } from "../../Button";
import language from "../../../language";
import axios from "axios";
import toastMessage from "../../../utils/toastMessage";
import { getStorage } from "../../../utils/storage";
import availableOptions from "../../../constants/availableOptions";
import { fetchPatients } from "../../../utils/queries/patient";
import {
  fetchPatientHospitalizations,
  savePatientHospitalization,
} from "../../../utils/queries/patient-hospitalization";
import fetchDepartments from "../../../utils/queries/departmentQuery";
import CheckboxQuestionnaire from "../../Input/CheckboxQuestionnaire";
import EmergencyFields from "./EmergencyFields";
import { fetchEmergencyOptions } from "../../../utils/queries/emergency";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";
import "react-datepicker/dist/react-datepicker.css";

import {
  generateRandomNumber,
  generateRandomString,
} from "../../../utils/random";
import moment from "moment";

const API_URL = process.env.REACT_APP_BBMS_BASE_API;

const statusOptions = [
  {
    label: "Pending",
    value: "pending",
  },
  {
    label: "Dispatched",
    value: "dispatched",
  },
  {
    label: "Delivered",
    value: "delivered",
  },
];

class NominativeRequest extends React.Component {
  state = {
    error: {},
    isSubmitting: false,
    donors: [],
    distributors: [],
    hospitals: [],
    numberOfPackages: "",
    isActive: availableOptions[0],
    description: "",
    approvals: [],
    packages: [],
    centers: [],
    centerSites: [],
    status: statusOptions[0],
    isBooking: false,
    isOrdinary: false,
    isUrgent: false,
    isPreviousTransfused: false,
    knownLabTestsSelected: [],
    bookingDate: new Date(),
    transfusionParams: [],
    transfusionParamsNote: "",
    lastTransfused: false,
    collectedBy: "",
    sampleCollectedTime: new Date(),
    hct: false,
    hgb: false,
    pltCount: false,
    pt: false,
    inr: false,
    ptt: false,
    fibronogen: false,
    // Settled before this form opened, so it is a prop rather than a
    // question. Kept in state only so an existing request being edited
    // still shows what it was raised as.
    requestType: "isOrdinary",
    consent: "",
    commitment: "",
    emergencyOptions: null,
    emergencyComponents: [],
    emergencyComponentsOther: "",
    emergencyReasons: [],
    emergencyReasonOther: "",
    emergencyBiological: {},
    patientHospitalizations: [],
    patientHospitalization: null,
    isFetchingPatientHospitalizations: false,
    showAddHospitalization: false,
    departments: [],
    isFetchingDepartments: false,
    newService: null,
    newRoomNumber: "",
    newBedNumber: "",
    isSavingHospitalization: false,
    requestingDoctorName: "",
    requestingDoctorContacts: "",
    requestingDoctorEmail: "",
  
    lastTransfusedDate: "",
    patient: null,
    requestNo: "",
};

  componentDidMount = async () => {
    const user = await getStorage();

    if (this.props.requestType) {
      this.setState({ requestType: this.props.requestType });
    }

    if (this.isEmergency()) {
      // The tick-list order and the wording both belong to the server, so
      // the form is built from what it sends rather than from a second copy
      // that could drift from the record being stored.
      try {
        this.setState({ emergencyOptions: await fetchEmergencyOptions() });
      } catch (error) {
        toastMessage("error", error);
      }
    }

    this.setState({
      requestNo:
        generateRandomString(4).toUpperCase() +
        "-" +
        generateRandomNumber(0, 1000000),
      requestingDoctorName: user.name || user.displayName || "",
      requestingDoctorContacts: user.phone || "",
      requestingDoctorEmail: user.email || "",
    });
  };

  // The kind of request is a prop now; state is only the fallback for a
  // request being reopened.
  isEmergency = () =>
    (this.props.requestType || this.state.requestType) === "isEmergencyRelease";

  // An emergency release does not ask the patient for consent - the doctor
  // has already taken the accountability, which is the whole point of the
  // statement they agreed to. So the rest of the form is open from the
  // start, rather than waiting on an answer that will never come.
  canShowForm = () =>
    this.isEmergency() ||
    this.state.consent === "yes" ||
    this.state.commitment !== "";

  requestTypeLabel = () => {
    const labels = {
      isOrdinary: "ordinary",
      isBooking: "booking",
      isEmergencyRelease: "emergency_release",
    };
    const key = labels[this.props.requestType || this.state.requestType];

    return key ? language[this.props.defaultLanguage][key] : "";
  };

  toggleEmergencyComponent = (value) => {
    this.setState((prev) => {
      const components = prev.emergencyComponents.includes(value)
        ? prev.emergencyComponents.filter((el) => el !== value)
        : [...prev.emergencyComponents, value];
      const error = { ...prev.error };

      delete error.components;

      return { emergencyComponents: components, error };
    });
  };

  onChangeEmergency = (field, value) => {
    this.setState((prev) => {
      const error = { ...prev.error };

      delete error[field];

      return {
        ["emergency" + field.charAt(0).toUpperCase() + field.slice(1)]:
          value,
        error,
      };
    });
  };

  onChangeEmergencyBiological = (field, value) => {
    this.setState((prev) => ({
      emergencyBiological: { ...prev.emergencyBiological, [field]: value },
    }));
  };

  onChangeText = async (field, e) => {
    let { error } = this.state;
    let inputValue = e && e?.target ? e?.target?.value : e;

    delete error[field];

    await this.setState({
      [field]: inputValue,
      error,
    });

    if (field === "patient") {
      await this.getPatientHospitalizations(true, inputValue?.value);
      if (!inputValue?.value) {
        this.setState({
          patientHospitalization: null,
          patientHospitalizations: [],
        });
      }
    }
  };

  onCheck(field, value) {
    this.setState(
      {
        [field]: value,
      },
      () => {
        console.log(`${field} updated to:`, this.state[field]);
      }
    );
  }

  getPatients = async (isFetchingPatient, search) => {
    try {
      this.setState({
        isFetchingPatient,
      });

      const data = await fetchPatients(this.props.defaultLanguage, {
        type: "dropdown",
        q: search ? search : undefined,
      });

      this.setState({
        patients: data,
        isFetchingPatient: false,
      });

      return data;
    } catch (error) {
      this.setState({ isFetchingPatient: false });
    }
  };

  getPatientHospitalizations = async (isFetchingPatientHospitalizations, patientId) => {
    try {
      this.setState({
        isFetchingPatientHospitalizations,
      });

      const user = await getStorage();
      const data = await fetchPatientHospitalizations(this.props.defaultLanguage, {
        type: "dropdown",
        patient: patientId,
        hospital: user?.hospital?._id || user?.hospital,
        isActive: true,
      });

      this.setState({
        patientHospitalizations: data || [],
        isFetchingPatientHospitalizations: false,
      });
    } catch (error) {
      this.setState({ isFetchingPatientHospitalizations: false });
    }
  };

  // getProducts = async (isFetchingProduct, search) => {
  //   try {
  //     this.setState({
  //       isFetchingProduct,
  //     });

  //     const data = await fetchProductTypes(this.props.defaultLanguage, {
  //       inputType: "dropdown",
  //       q: search ? search : undefined,
  //       page: 1,
  //       limit: 100,
  //     });

  //     this.setState({
  //       isFetchingProduct: false,
  //     });

  //     return data;
  //   } catch (error) {
  //     this.setState({ isFetchingProduct: false });
  //   }
  // };

  getDepartments = async (search) => {
    try {
      this.setState({ isFetchingDepartments: true });
      const data = await fetchDepartments(this.props.defaultLanguage, {
        type: "dropdown",
        q: search || undefined,
      });
      this.setState({ departments: data || [], isFetchingDepartments: false });
      return data;
    } catch (error) {
      this.setState({ isFetchingDepartments: false });
    }
  };

  saveNewHospitalization = async () => {
    const { newService, newRoomNumber, newBedNumber, patient } = this.state;
    if (!newService || !newRoomNumber || !newBedNumber) return;

    this.setState({ isSavingHospitalization: true });
    try {
      const user = await getStorage();
      await savePatientHospitalization(this.props.defaultLanguage, {
        patient: patient?.value,
        hospital: user?.hospital?._id || user?.hospital,
        service: newService?.value,
        roomNumber: newRoomNumber,
        bedNumber: newBedNumber,
        isActive: true,
      });
      this.setState({
        showAddHospitalization: false,
        newService: null,
        newRoomNumber: "",
        newBedNumber: "",
        isSavingHospitalization: false,
      });
      await this.getPatientHospitalizations(true, patient?.value);
    } catch (error) {
      this.setState({ isSavingHospitalization: false });
    }
  };

  onChangeDate(field, date) {
    this.setState({
      [field]: date,
    });
  }

  validateForm() {
// eslint-disable-next-line no-unused-vars
    let { error, requestNo, patient, requestProduct, patientHospitalization } =
      this.state;

    if (!patient) {
      error.patient = language[this.props.defaultLanguage].patient_required;
    }
    if (!patientHospitalization) {
      error.patientHospitalization =
        language[this.props.defaultLanguage].service_required;
    }

    if (requestNo === "") {
      error.requestNo =
        language[this.props.defaultLanguage].request_number_required;
    }

    if (this.isEmergency()) {
      const {
        emergencyComponents,
        emergencyComponentsOther,
        emergencyReasons,
        emergencyReasonOther,
      } = this.state;

      if (!emergencyComponents.length) {
        error.components =
          language[this.props.defaultLanguage].emergency_components_required;
      }

      if (emergencyComponents.includes("OTHER") &&
        !emergencyComponentsOther.trim()) {
        error.componentsOther =
          language[this.props.defaultLanguage].please_specify;
      }

      if (!emergencyReasons.length) {
        error.reasons =
          language[this.props.defaultLanguage].reason_required;
      }

      if (emergencyReasons.includes("OTHER") && !emergencyReasonOther.trim()) {
        error.reasonOther =
          language[this.props.defaultLanguage].please_specify;
      }

      // The biological parameters are deliberately not checked: a doctor
      // releasing blood for an unidentified patient has none of them, and
      // the one request that cannot wait is not the one to hold up for a
      // haematocrit.
    }

    // if (!requestProduct) {
    //   error.requestProduct =
    //     language[this.props.defaultLanguage].request_product_required;
    // }

    this.setState({ error });
  }

  onSubmit = async () => {
    await this.validateForm();

    if (Object.keys(this.state.error).length === 0) {
      this.setState({
        isSubmitting: true,
      });

      let {
          _id,
          description,
          status,
          requestNo,
          isUrgent,
          clinicalInformation,
          patient,
          patientHospitalization,
          historyPregnancy,
          testRequired,
          requestType,
          requestProduct,
          consent,
          commitment,
          requestingDoctorName,
          requestingDoctorContacts,
          requestingDoctorEmail,
        } = this.state,
        url = `${API_URL}/bloodRequest`,
        method = "POST",
        user = await getStorage();

      let requestedBody = {
        type: "nominative",
        requestNo,
        hospital: user.hospital,
        description: description,
        bloodBank: user?.stock,
        account: user.id,
        clinicalInformation,
        patient: patient?.value,
        patientHospitalization: patientHospitalization?.value,
        historyPregnancy:
          historyPregnancy === undefined ? false : historyPregnancy,
        isUrgent,
        isOrdinary: this.state.isOrdinary,
        requestType,
        testRequired: testRequired,
        previouslyTransfused: this.state.lastTransfused
          ? this.state.lastTransfusedDate
          : "",
        status: "initiated",
        products: requestProduct?.map((el) => el.value),
        consent: consent === "yes" ? true : false,
        commitment,
        requestingDoctorName,
        requestingDoctorContacts,
        requestingDoctorEmail,
      };

      // Only a booking has a booked time. This used to be sent on every
      // nominative request, stamped with the moment it was created, which
      // put ordinary requests and emergency releases into the booking sweep
      // and had the laboratory chased about a slot that was never booked.
      if ((this.props.requestType || this.state.requestType) === "isBooking") {
        requestedBody.bookingDate = this.state.bookingDate;
      }

      if (this.isEmergency()) {
        // Only the agreement itself is sent. The statement, the timestamp
        // and whose name goes against it are written by the server, so a
        // client cannot post one wording and have another recorded.
        requestedBody.emergencyRelease = {
          components: this.state.emergencyComponents,
          componentsOther: this.state.emergencyComponentsOther,
          reasons: this.state.emergencyReasons,
          reasonOther: this.state.emergencyReasonOther,
          biological: this.state.emergencyBiological,
          physicianAuthorization: { agreed: true },
        };
      }

      if (status?.value) {
        requestedBody.status = status.value;
      }

      if (_id && _id !== "") {
        method = "PUT";
        requestedBody.id = _id;
      }

      const options = {
        method,
        url,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + user.token,
        },
        data: requestedBody,
      };

      axios(options)
        .then((data) => {
          this.setState({
            isSubmitting: false,
          });

          toastMessage(
            "success",
            language[this.props.defaultLanguage][
              method === "PUT"
                ? "update_bloodrequest_success"
                : "add_bloodrequest_success"
            ]
          );

          this.props.handleCloseModal();
          window.location.href = `/dashboard/request/${requestNo}`;
        })
        .catch((error) => {
          toastMessage("error", error);
          this.setState({
            isSubmitting: false,
          });
        });
    }
  };

  handleConsent = (action) => {
    this.setState({
      consent: action,
      commitment: action === "yes" ? "" : this.state.commitment,
    });
  };

  render() {
    return (
      <div>
        <div className="card-body">
          {/* An emergency release carries the doctor's own accountability,
              agreed before this form opened, in place of the patient's
              consent - so the question is not put at all. */}
          {!this.isEmergency() && (
          <div className="col-md-12">
            <p>
              Have you provided a clear explanation to the patient/guardian of
              why the transfusion is needed, including the specific blood
              component and the clinical indication, how the transfusion is
              expected to help the patient as well as potential risks? Do they
              accept to be transfused?
            </p>
            <br />
            {this.props._id ? (
              <p className="text-primary text-bold">
                {this.props.consent
                  ? language[this.props.defaultLanguage].yes
                  : language[this.props.defaultLanguage].no}
              </p>
            ) : (
              <div className="d-flex align-items-center gap-3">
                <Button
                  text={language[this.props.defaultLanguage].yes}
                  onPress={this.handleConsent.bind(this, "yes")}
                />
                <Button
                  text={language[this.props.defaultLanguage].no}
                  className="btn-default"
                  onPress={this.handleConsent.bind(this, "no")}
                />
              </div>
            )}
          </div>
          )}
          {!this.isEmergency() && this.state.consent === "no" && (
            <div className="col-md-12">
              <Input
                placeholder={
                  language[this.props.defaultLanguage].commitment_placeholder
                }
                label={
                  language[this.props.defaultLanguage].commitment +
                  " / " +
                  language[this.props.defaultLanguage].motivation
                }
                required
                value={this.state.commitment}
                onChange={(e) => this.onChangeText("commitment", e)}
                error={this.state.error.commitment}
                textarea
              />
            </div>
          )}
          {this.canShowForm() && (
            <>
              <div className="col-md-12">
                <Input
                  placeholder={
                    language[this.props.defaultLanguage]
                      .request_number_placeholder
                  }
                  label={language[this.props.defaultLanguage].request_number}
                  required
                  value={this.state.requestNo}
                  onChange={(e) => this.onChangeText("requestNo", e)}
                  error={this.state.error.requestNo}
                />
              </div>
              <div className="col-md-12">
                <Select
                  loadOptions={(e) => this.getPatients(true, e)}
                  placeholder={language[this.props.defaultLanguage].select}
                  label={language[this.props.defaultLanguage].patient}
                  required
                  value={this.state.patient}
                  onChange={(e) => this.onChangeText("patient", e)}
                  error={this.state.error.patient}
                  isLoading={this.state.isFetchingPatient}
                />
              </div>
              <div className="col-md-12">
                <Select
                  options={this.state.patientHospitalizations}
                  placeholder={language[this.props.defaultLanguage].select}
                  label={language[this.props.defaultLanguage].hospitalize_service}
                  required
                  value={this.state.patientHospitalization}
                  onChange={(e) => this.onChangeText("patientHospitalization", e)}
                  error={this.state.error.patientHospitalization}
                  isLoading={this.state.isFetchingPatientHospitalizations}
                />
                {this.state.patient &&
                  !this.state.isFetchingPatientHospitalizations &&
                  this.state.patientHospitalizations.length === 0 && (
                    <div style={{ marginTop: "-8px", marginBottom: "8px" }}>
                      {!this.state.showAddHospitalization ? (
                        <button
                          type="button"
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--primary-color)",
                            cursor: "pointer",
                            fontSize: "var(--font-size-sm)",
                            padding: 0,
                            textDecoration: "underline",
                          }}
                          onClick={() => {
                            this.setState({ showAddHospitalization: true });
                            this.getDepartments();
                          }}
                        >
                          + Add hospitalization for this patient
                        </button>
                      ) : (
                        <div
                          style={{
                            border: "1px solid var(--border-color)",
                            borderRadius: "6px",
                            padding: "12px",
                            marginTop: "4px",
                            background: "var(--light-color)",
                          }}
                        >
                          <p style={{ margin: "0 0 8px", fontWeight: 600, fontSize: "var(--font-size-sm)" }}>
                            New Hospitalization
                          </p>
                          <Select
                            options={this.state.departments}
                            placeholder="Select service / department"
                            label="Service"
                            required
                            value={this.state.newService}
                            onChange={(e) => this.setState({ newService: e })}
                            isLoading={this.state.isFetchingDepartments}
                          />
                          <Input
                            label="Room Number"
                            placeholder="e.g. 204"
                            required
                            value={this.state.newRoomNumber}
                            onChange={(e) => this.setState({ newRoomNumber: e?.target?.value ?? e })}
                          />
                          <Input
                            label="Bed Number"
                            placeholder="e.g. B"
                            required
                            value={this.state.newBedNumber}
                            onChange={(e) => this.setState({ newBedNumber: e?.target?.value ?? e })}
                          />
                          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                            <Button
                              text="Save Hospitalization"
                              onPress={this.saveNewHospitalization}
                              isSubmitting={this.state.isSavingHospitalization}
                              disabled={
                                !this.state.newService ||
                                !this.state.newRoomNumber ||
                                !this.state.newBedNumber
                              }
                            />
                            <Button
                              text="Cancel"
                              className="btn-default"
                              onPress={() =>
                                this.setState({
                                  showAddHospitalization: false,
                                  newService: null,
                                  newRoomNumber: "",
                                  newBedNumber: "",
                                })
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
              <div className="row">
                {/* Asked before this form opened, so it is shown back
                    rather than put again. Answering it twice is how a
                    doctor ends up with a request that is not the kind they
                    accepted the accountability for. */}
                <div className="col-md-12">
                  <Input
                    label={language[this.props.defaultLanguage].request_type}
                    value={this.requestTypeLabel()}
                    disabled
                  />
                </div>

                {this.isEmergency() && (
                  <EmergencyFields
                    options={this.state.emergencyOptions}
                    components={this.state.emergencyComponents}
                    componentsOther={this.state.emergencyComponentsOther}
                    reasons={this.state.emergencyReasons}
                    reasonOther={this.state.emergencyReasonOther}
                    biological={this.state.emergencyBiological}
                    error={this.state.error}
                    onToggleComponent={this.toggleEmergencyComponent}
                    onChange={this.onChangeEmergency}
                    onChangeBiological={this.onChangeEmergencyBiological}
                  />
                )}

                {this.state.requestType === "isBooking" && (
                  <div className="col-md-12">
                    <div
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: "5px",
                        padding: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "10px",
                      }}
                    >
                      <Input
                        label={
                          language[this.props.defaultLanguage]
                            .select_booking_date_time
                        }
                        onChange={(e) => this.onChangeText("bookingDate", e)}
                        value={this.state.bookingDate}
                        type="datetime-local"
                        min={moment().format("YYYY-MM-DD HH:mm")}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-12">
                  <CheckboxQuestionnaire
                    questionText={
                      // language[this.props.defaultLanguage].history_of_transfusion
                      "Has the patient received a blood transfusion in the past?"
                    }
                    // Has the patient received a blood transfusion in the past?
                    answers={[
                      {
                        field: "lastTransfused",
                        value: language[this.props.defaultLanguage].yes,
                        onPress: () => this.onCheck("lastTransfused", true),
                      },
                      {
                        field: "lastTransfused",
                        value: language[this.props.defaultLanguage].no,
                        onPress: () => this.onCheck("lastTransfused", false),
                      },
                    ]}
                    checked={this.state.lastTransfused === true ? "Yes" : "No"}
                    error={this.state.error.lastTransfused}
                  />

                  {this.state.lastTransfused && (
                    <div className="col-md-12">
                      <div
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                          padding: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "20px",
                        }}
                      >
                        <Input
                          label={
                            language[this.props.defaultLanguage]
                              .last_transfused_date
                          }
                          value={this.state.lastTransfusedDate}
                          onChange={(e) =>
                            this.onChangeText("lastTransfusedDate", e)
                          }
                          type="date"
                        />
                      </div>
                    </div>
                  )}
                  <CheckboxQuestionnaire
                    questionText={
                      language[this.props.defaultLanguage].history_of_pregnancy
                    }
                    answers={[
                      {
                        field: "historyPregnancy",
                        value: language[this.props.defaultLanguage].yes,
                        onPress: () => this.onCheck("historyPregnancy", true),
                      },
                      {
                        field: "historyPregnancy",
                        value: language[this.props.defaultLanguage].no,
                        onPress: () => this.onCheck("historyPregnancy", false),
                      },
                    ]}
                    checked={
                      this.state.historyPregnancy === true ? "Yes" : "No"
                    }
                    error={this.state.error.historyPregnancy}
                  />
                </div>
              </div>
            </>
          )}
        </div>
        {this.canShowForm() && (
          <div className="row" style={{ padding: "0 1rem" }}>
            <div className="col-md-4">
              <Input
                label="Requesting Doctor Name"
                placeholder="Enter doctor's full name"
                value={this.state.requestingDoctorName}
                onChange={(e) => this.onChangeText("requestingDoctorName", e)}
                error={this.state.error.requestingDoctorName}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Requesting Doctor Contacts"
                placeholder="Enter doctor's phone number"
                value={this.state.requestingDoctorContacts}
                onChange={(e) => this.onChangeText("requestingDoctorContacts", e)}
                error={this.state.error.requestingDoctorContacts}
              />
            </div>
            <div className="col-md-4">
              <Input
                label="Requesting Doctor Email"
                placeholder="Enter doctor's email"
                value={this.state.requestingDoctorEmail}
                onChange={(e) => this.onChangeText("requestingDoctorEmail", e)}
                error={this.state.error.requestingDoctorEmail}
              />
            </div>
          </div>
        )}
        {this.canShowForm() && (
          <div className="modal-footer">
            <Button
              text={language[this.props.defaultLanguage].cancel}
              onPress={this.props.handleCloseModal}
              className="btn-default"
            />
            <Button
              text={language[this.props.defaultLanguage].submit}
              onPress={this.onSubmit.bind(this)}
              isSubmitting={this.state.isSubmitting}
              // An emergency release never answers the consent question, so a
              // gate on it would leave Submit dead for the one kind of request
              // that cannot wait. The form is open on the same answer either way.
              disabled={!this.canShowForm() || !this.state.patient}
            />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return {
    defaultLanguage,
  };
};

export default connect(mapStateToProps)(NominativeRequest);
